import { useCallback, useEffect, useRef, useState } from 'react';

export const DEVICE_LOCK_KEY = 'enara_device_lock';
const LOCK_KEY = DEVICE_LOCK_KEY;
const CHANNEL_NAME = 'enara-device-lock';
const HEARTBEAT_INTERVAL_MS = 5000;
const STALE_THRESHOLD_MS = 10000;

interface LockData {
  tabId: string;
  timestamp: number;
}

interface UseDeviceLockReturn {
  acquireLock: () => boolean;
  releaseLock: () => void;
  isLockedByOtherTab: boolean;
}

function readLock(): LockData | null {
  try {
    const raw = localStorage.getItem(LOCK_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as LockData;
  } catch {
    return null;
  }
}

function isLockStale(lock: LockData): boolean {
  return Date.now() - lock.timestamp > STALE_THRESHOLD_MS;
}

export function useDeviceLock(): UseDeviceLockReturn {
  const tabIdRef = useRef(crypto.randomUUID());
  const heartbeatRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const channelRef = useRef<BroadcastChannel | null>(null);
  const [isLockedByOtherTab, setIsLockedByOtherTab] = useState(() => {
    const lock = readLock();
    return lock !== null && lock.tabId !== tabIdRef.current && !isLockStale(lock);
  });

  const stopHeartbeat = useCallback(() => {
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
  }, []);

  const releaseLock = useCallback(() => {
    const lock = readLock();
    if (lock?.tabId === tabIdRef.current) {
      localStorage.removeItem(LOCK_KEY);
    }
    stopHeartbeat();
    try {
      channelRef.current?.postMessage({ type: 'LOCK_RELEASED', tabId: tabIdRef.current });
    } catch {
      // Channel may already be closed during cleanup
    }
  }, [stopHeartbeat]);

  const acquireLock = useCallback((): boolean => {
    const existing = readLock();

    if (existing && existing.tabId !== tabIdRef.current && !isLockStale(existing)) {
      return false;
    }

    const lockData: LockData = { tabId: tabIdRef.current, timestamp: Date.now() };
    localStorage.setItem(LOCK_KEY, JSON.stringify(lockData));

    try {
      channelRef.current?.postMessage({ type: 'LOCK_ACQUIRED', tabId: tabIdRef.current });
    } catch {
      // Channel may not be available
    }

    stopHeartbeat();
    heartbeatRef.current = setInterval(() => {
      const current = readLock();
      if (current?.tabId === tabIdRef.current) {
        localStorage.setItem(
          LOCK_KEY,
          JSON.stringify({ tabId: tabIdRef.current, timestamp: Date.now() }),
        );
      }
    }, HEARTBEAT_INTERVAL_MS);

    return true;
  }, [stopHeartbeat]);

  // Set up BroadcastChannel and storage event listeners
  useEffect(() => {
    const channel = new BroadcastChannel(CHANNEL_NAME);
    channelRef.current = channel;

    channel.onmessage = (event) => {
      const { type, tabId } = event.data;
      if (tabId === tabIdRef.current) return;

      if (type === 'LOCK_ACQUIRED') {
        setIsLockedByOtherTab(true);
      } else if (type === 'LOCK_RELEASED') {
        setIsLockedByOtherTab(false);
      }
    };

    // Fallback: listen for storage events (fires in other tabs when localStorage changes)
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key !== LOCK_KEY) return;

      if (!event.newValue) {
        setIsLockedByOtherTab(false);
        return;
      }

      try {
        const lock = JSON.parse(event.newValue) as LockData;
        setIsLockedByOtherTab(lock.tabId !== tabIdRef.current && !isLockStale(lock));
      } catch {
        // Ignore malformed data
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // Synchronous cleanup on tab close
    const handleBeforeUnload = () => {
      const lock = readLock();
      if (lock?.tabId === tabIdRef.current) {
        localStorage.removeItem(LOCK_KEY);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      releaseLock();
      channel.close();
      channelRef.current = null;
    };
  }, [releaseLock]);

  return { acquireLock, releaseLock, isLockedByOtherTab };
}
